import { useLoaderData } from 'react-router';

import { useBookMeetingGateEnabled } from '~/modules-rocco/booking/book-meeting-gate-state';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

/**
 * Returns the list of bookable Client Director CalendarSessions linked to the
 * project. Both one-on-one and round-robin sessions are included by design; only
 * active sessions whose consultant has a connected calendar are surfaced —
 * otherwise availability can't return any slots. Merges the many_many
 * `ClientDirectorCalendarSessions` with the legacy has_one
 * `ClientDirectorCalendarSession` for backward compatibility.
 * `canBookDirect` is true when at least one bookable session exists.
 *
 * {@link useBookMeetingGateEnabled} gates direct booking: with the gate off no
 * sessions are surfaced and `canBookDirect` is false, so every "Book a Meeting"
 * CTA falls back to the standard enquiry flow — the (03) accordion row renders
 * as "Get in touch" with the inline enquiry form, and the summary/island CTAs
 * open the enquiry modal.
 *
 * `bookMeetingEnabled` is that gate on its own. The (03) row needs it separately
 * from `canBookDirect`, because with the gate on but no session linked it shows
 * the booking card in request mode rather than the enquiry form.
 */
export const useProjectBookMeeting = () => {
    const { project } = useLoaderData<typeof loader>();
    const bookMeetingEnabled = useBookMeetingGateEnabled();

    const manySessions = (project.ClientDirectorCalendarSessions ?? []).filter(
        (s): s is NonNullable<typeof s> =>
            s !== null && s.IsActive && s.HasConnectedConsultant,
    );

    // Fallback: honour the legacy has_one if the many_many list is empty.
    const legacySession = project.ClientDirectorCalendarSession;
    const legacyValid =
        Boolean(
            legacySession?.IsActive && legacySession.HasConnectedConsultant,
        ) && !manySessions.some((s) => s.ID === legacySession?.ID);

    const linkedSessions =
        legacyValid && legacySession
            ? [...manySessions, legacySession]
            : manySessions;

    const sessions = bookMeetingEnabled ? linkedSessions : [];
    const canBookDirect = sessions.length > 0;

    return { canBookDirect, sessions, bookMeetingEnabled };
};
